import BookmarksIcon from '@mui/icons-material/Bookmarks'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import FilterListIcon from '@mui/icons-material/FilterList'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveIcon from '@mui/icons-material/Save'
import SearchIcon from '@mui/icons-material/Search'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import UpdateIcon from '@mui/icons-material/Update'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-pro'
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd'
import { Vehicle } from '@otw/models/inventory'
import { PdfColumnConfig } from '@/core/inventory/downloadShareableInventoryPdf'
import { useMemo, useState } from 'react'
import { ActionsPanel } from '@/components/inventory/actions/ActionsPanel'
import { DEFAULT_V3_TEMPLATE_ID, ensureCheckboxPinnedLeft } from './inventoryV3DefaultTemplate'
import { InventoryV3Row } from './buildInventoryV3Rows'
import { InventoryV3Template } from './useInventoryV3Templates'
import type { InventoryV3SidebarTab } from './inventoryV3Route'
import { isInventoryV3FilterItemActive } from './applyInventoryV3FilterModel'
import { InventoryV3DatePicker, InventoryV3DateRangePicker } from './inventoryV3DateFilterOperators'

export type { InventoryV3SidebarTab }

const RAIL_WIDTH = 56
const PANEL_WIDTH = 320

const PINNED_LEFT_DROPPABLE_ID = 'inventory-v3-pinned-left'
const PINNED_RIGHT_DROPPABLE_ID = 'inventory-v3-pinned-right'

const reorderList = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

/** Grid-managed columns the user should never be able to hide from the panel. */
const UNMANAGED_FIELDS = new Set(['__check__', '__detail_panel_toggle__'])

interface InventoryV3SidebarProps {
  tab: InventoryV3SidebarTab | null
  onTabChange: (tab: InventoryV3SidebarTab | null) => void

  columns: GridColDef<InventoryV3Row>[]
  columnVisibilityModel: GridColumnVisibilityModel
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void
  pinnedColumns: GridPinnedColumnFields
  onPinnedColumnsChange: (model: GridPinnedColumnFields) => void
  onResetColumns: () => void

  templates: InventoryV3Template[]
  activeTemplateId: string | null
  favoriteTemplateId: string
  onApplyTemplate: (templateId: string) => void
  onSetFavoriteTemplate: (templateId: string) => void
  onSaveTemplate: () => void
  onUpdateTemplate: () => void
  onDeleteTemplate: () => void
  isSavingTemplate: boolean

  selectedVehicles: Vehicle[]
  filterModel: GridFilterModel
  onFilterModelChange: (model: GridFilterModel) => void
  clearAllFilters: () => void
  hasActiveFilters: boolean
  getVisibleColumnsForPdf: () => PdfColumnConfig[]
}

const RAIL_ITEMS: { tab: InventoryV3SidebarTab; label: string; icon: JSX.Element }[] = [
  { tab: 'columns', label: 'Columns', icon: <ViewColumnIcon fontSize='small' /> },
  { tab: 'filters', label: 'Filters', icon: <FilterListIcon fontSize='small' /> },
  { tab: 'templates', label: 'Templates', icon: <BookmarksIcon fontSize='small' /> },
  { tab: 'actions', label: 'Actions', icon: <PlaylistAddCheckIcon fontSize='small' /> },
]

const OPERATORS_WITHOUT_VALUE = new Set(['isEmpty', 'isNotEmpty'])

const DEFAULT_OPERATORS: Record<string, { value: string; label: string }[]> = {
  string: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'endsWith', label: 'ends with' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
  number: [
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '>=', label: '>=' },
    { value: '<', label: '<' },
    { value: '<=', label: '<=' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
  date: [
    { value: 'is', label: 'On' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
    { value: 'between', label: 'Between' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
  boolean: [{ value: 'is', label: 'is' }],
  singleSelect: [{ value: 'is', label: 'is' }],
}

const operatorsForColumn = (column: GridColDef<InventoryV3Row> | undefined) => {
  if (column?.filterOperators?.length) {
    // Use column operators as declared (singleSelect includes isAnyOf for multi-select).
    return column.filterOperators.map(operator => ({
      value: operator.value,
      label: operator.label ?? operator.value,
    }))
  }
  const type = column?.type === 'dateTime' ? 'date' : (column?.type ?? 'string')
  return DEFAULT_OPERATORS[type] ?? DEFAULT_OPERATORS.string
}

const emptyFilterValue = (operator: string) => {
  if (OPERATORS_WITHOUT_VALUE.has(operator)) return undefined
  if (operator === 'isAnyOf') return []
  if (operator === 'between') return ['', '']
  return ''
}

const normalizeFilterValueForOperator = (operator: string, value: unknown) => {
  if (OPERATORS_WITHOUT_VALUE.has(operator)) return undefined
  if (operator === 'between') {
    if (Array.isArray(value)) return [value[0] == null ? '' : String(value[0]), value[1] == null ? '' : String(value[1])]
    if (typeof value === 'string' && value.trim()) return [value, '']
    return ['', '']
  }
  if (operator === 'isAnyOf') {
    if (Array.isArray(value)) return value.map(String)
    if (value === null || value === undefined || value === '') return []
    return [String(value)]
  }
  if (Array.isArray(value)) return value.length > 0 ? String(value[0]) : ''
  return value ?? ''
}

const valueOptionsForColumn = (column: GridColDef<InventoryV3Row> | undefined) => {
  if (!column || !('valueOptions' in column)) return null
  const options = column.valueOptions
  if (!options) return null
  const resolved =
    typeof options === 'function'
      ? // Header/sidebar have no row — model/size fall back to full unions.
        options({ field: column.field } as Parameters<typeof options>[0])
      : options
  if (!Array.isArray(resolved)) return null
  return resolved.map(option =>
    typeof option === 'object' && option !== null
      ? {
          value: String((option as { value: unknown }).value),
          label: String((option as { label?: unknown }).label ?? (option as { value: unknown }).value),
        }
      : { value: String(option), label: String(option) },
  )
}

const nextFilterId = () => `inv-v3-filter-${Date.now()}-${Math.round(Math.random() * 1e5)}`

const createFilterItem = (column: GridColDef<InventoryV3Row>): GridFilterItem => {
  const operators = operatorsForColumn(column)
  // Prefer multi-select when the column offers it (singleSelect isAnyOf).
  const operator =
    operators.find(candidate => candidate.value === 'isAnyOf')?.value ??
    operators[0]?.value ??
    'contains'
  return {
    id: nextFilterId(),
    field: column.field,
    operator,
    value: emptyFilterValue(operator),
  }
}

const PanelHeader = ({ title, caption }: { title: string; caption?: string }) => (
  <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {caption ? (
      <Typography variant='caption' color='text.secondary'>
        {caption}
      </Typography>
    ) : null}
  </Box>
)

const PinnedColumnsReorder = ({
  columns,
  pinnedColumns,
  onPinnedColumnsChange,
}: {
  columns: GridColDef<InventoryV3Row>[]
  pinnedColumns: GridPinnedColumnFields
  onPinnedColumnsChange: (model: GridPinnedColumnFields) => void
}) => {
  const theme = useTheme()

  const labelByField = useMemo(
    () => new Map(columns.map(column => [column.field, column.headerName ?? column.field])),
    [columns],
  )

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const { source, destination } = result
    if (source.droppableId !== destination.droppableId) return

    const key = source.droppableId === PINNED_LEFT_DROPPABLE_ID ? 'left' : 'right'
    const current = (pinnedColumns[key] ?? []).filter(field => !UNMANAGED_FIELDS.has(field))
    const next = reorderList(current, source.index, destination.index)
    onPinnedColumnsChange(ensureCheckboxPinnedLeft({ ...pinnedColumns, [key]: next }))
  }

  const left = (pinnedColumns.left ?? []).filter(field => !UNMANAGED_FIELDS.has(field))
  const right = (pinnedColumns.right ?? []).filter(field => !UNMANAGED_FIELDS.has(field))

  const renderSide = (droppableId: string, side: 'left' | 'right', fields: string[]) => {
    if (fields.length === 0) return null

    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <PushPinIcon
            sx={{
              fontSize: '0.85rem',
              color: theme.palette.grey[600],
              transform: side === 'right' ? 'scaleX(-1)' : 'none',
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: theme.palette.grey[700],
            }}
          >
            {side === 'left' ? 'Left' : 'Right'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: theme.palette.grey[600] }}>
            ({fields.length})
          </Typography>
        </Box>

        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                p: 0.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: snapshot.isDraggingOver ? theme.palette.primary.main : theme.palette.grey[300],
                bgcolor: snapshot.isDraggingOver ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
              }}
            >
              {fields.map((field, index) => (
                <Draggable key={field} draggableId={field} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        pl: 0.5,
                        pr: 1,
                        py: 0.5,
                        mb: index === fields.length - 1 ? 0 : 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: dragSnapshot.isDragging
                          ? theme.palette.primary.main
                          : theme.palette.grey[300],
                        bgcolor: dragSnapshot.isDragging
                          ? theme.palette.common.white
                          : theme.palette.grey[100],
                        boxShadow: dragSnapshot.isDragging ? 3 : 'none',
                        '&:hover': { borderColor: theme.palette.grey[500] },
                      }}
                    >
                      <Box
                        {...dragProvided.dragHandleProps}
                        aria-label={`Reorder ${labelByField.get(field) ?? field}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: theme.palette.grey[600],
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                          '&:hover': { color: theme.palette.primary.main },
                        }}
                      >
                        <DragHandleIcon sx={{ fontSize: '1.05rem' }} />
                      </Box>

                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          flexShrink: 0,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography
                        noWrap
                        sx={{ fontSize: 13, flex: 1, minWidth: 0, color: 'text.primary', fontWeight: 500 }}
                      >
                        {labelByField.get(field) ?? field}
                      </Typography>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Box>
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 600, color: 'text.primary' }}>
          Pinned column order
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: theme.palette.grey[600], mt: 0.25 }}>
          Drag a row to change where it sits in the frozen area.
        </Typography>

        {/* Capped so a long pin list cannot squeeze the column checklist above. */}
        <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
          {renderSide(PINNED_LEFT_DROPPABLE_ID, 'left', left)}
          {renderSide(PINNED_RIGHT_DROPPABLE_ID, 'right', right)}
        </Box>
      </Box>
    </DragDropContext>
  )
}

const ColumnsPanel = ({
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  pinnedColumns,
  onPinnedColumnsChange,
  onResetColumns,
}: Pick<
  InventoryV3SidebarProps,
  | 'columns'
  | 'columnVisibilityModel'
  | 'onColumnVisibilityModelChange'
  | 'pinnedColumns'
  | 'onPinnedColumnsChange'
  | 'onResetColumns'
>) => {
  const [search, setSearch] = useState('')

  const manageable = useMemo(
    () => columns.filter(column => !UNMANAGED_FIELDS.has(column.field)),
    [columns],
  )

  const visibleCount = useMemo(
    () => manageable.filter(column => columnVisibilityModel[column.field] !== false).length,
    [manageable, columnVisibilityModel],
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return manageable
    return manageable.filter(column =>
      `${column.headerName ?? ''} ${column.field}`.toLowerCase().includes(term),
    )
  }, [manageable, search])

  const setAll = (visible: boolean) => {
    onColumnVisibilityModelChange({
      ...columnVisibilityModel,
      ...Object.fromEntries(filtered.map(column => [column.field, visible])),
    })
  }

  const toggle = (field: string) => {
    onColumnVisibilityModelChange({
      ...columnVisibilityModel,
      [field]: columnVisibilityModel[field] === false,
    })
  }

  const setPinSide = (field: string, side: 'left' | 'right' | null) => {
    const left = (pinnedColumns.left ?? []).filter(item => item !== field)
    const right = (pinnedColumns.right ?? []).filter(item => item !== field)
    if (side === 'left') left.push(field)
    if (side === 'right') right.push(field)
    onPinnedColumnsChange(ensureCheckboxPinnedLeft({ ...pinnedColumns, left, right }))
  }

  return (
    <>
      <PanelHeader title='Columns' caption={`${visibleCount} of ${manageable.length} shown`} />

      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          size='small'
          fullWidth
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder='Find a column'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => setSearch('')} aria-label='Clear column search'>
                  <ClearIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
          <Button size='small' onClick={() => setAll(true)} sx={{ textTransform: 'none', flex: 1 }}>
            Show all
          </Button>
          <Button size='small' onClick={() => setAll(false)} sx={{ textTransform: 'none', flex: 1 }}>
            Hide all
          </Button>
          <Tooltip title='Reset columns to the default view'>
            <IconButton size='small' onClick={onResetColumns} aria-label='Reset columns'>
              <RestartAltIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <List dense disablePadding>
          {filtered.map(column => {
            const isVisible = columnVisibilityModel[column.field] !== false
            const pinnedLeft = (pinnedColumns.left ?? []).includes(column.field)
            const pinnedRight = (pinnedColumns.right ?? []).includes(column.field)
            const label = column.headerName ?? column.field
            return (
              <ListItemButton
                key={column.field}
                onClick={() => toggle(column.field)}
                sx={{ py: 0.25, pl: 1, pr: 0.5 }}
              >
                <Checkbox size='small' checked={isVisible} tabIndex={-1} disableRipple sx={{ p: 0.5, mr: 0.5 }} />
                <ListItemText
                  primary={column.headerName || column.field}
                  primaryTypographyProps={{
                    fontSize: 13,
                    noWrap: true,
                    color: isVisible ? 'text.primary' : 'text.secondary',
                  }}
                />
                <Tooltip title={pinnedLeft ? 'Unpin from left' : 'Pin to left'}>
                  <IconButton
                    size='small'
                    aria-label={pinnedLeft ? `Unpin ${label} from left` : `Pin ${label} to left`}
                    onClick={event => {
                      event.stopPropagation()
                      setPinSide(column.field, pinnedLeft ? null : 'left')
                    }}
                    sx={{ opacity: pinnedLeft ? 1 : 0.35 }}
                  >
                    {pinnedLeft ? (
                      <PushPinIcon sx={{ fontSize: '1rem' }} />
                    ) : (
                      <PushPinOutlinedIcon sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={pinnedRight ? 'Unpin from right' : 'Pin to right'}>
                  <IconButton
                    size='small'
                    aria-label={pinnedRight ? `Unpin ${label} from right` : `Pin ${label} to right`}
                    onClick={event => {
                      event.stopPropagation()
                      setPinSide(column.field, pinnedRight ? null : 'right')
                    }}
                    sx={{ opacity: pinnedRight ? 1 : 0.35 }}
                  >
                    {pinnedRight ? (
                      <PushPinIcon sx={{ fontSize: '1rem', transform: 'scaleX(-1)' }} />
                    ) : (
                      <PushPinOutlinedIcon sx={{ fontSize: '1rem', transform: 'scaleX(-1)' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            )
          })}
          {filtered.length === 0 && (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', px: 2, py: 2 }}>
              No columns match “{search}”.
            </Typography>
          )}
        </List>
      </Box>

      {(pinnedColumns.left ?? []).some(field => !UNMANAGED_FIELDS.has(field)) ||
      (pinnedColumns.right ?? []).some(field => !UNMANAGED_FIELDS.has(field)) ? (
        <>
          <Divider />
          <PinnedColumnsReorder
            columns={columns}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={onPinnedColumnsChange}
          />
        </>
      ) : null}

      <Divider />
      <Typography variant='caption' sx={{ px: 2, py: 1, color: 'grey.600' }}>
        Drag column headers in the grid to change unpinned column order.
      </Typography>
    </>
  )
}

const TemplatesPanel = ({
  templates,
  activeTemplateId,
  favoriteTemplateId,
  onApplyTemplate,
  onSetFavoriteTemplate,
  onSaveTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  isSavingTemplate,
}: Pick<
  InventoryV3SidebarProps,
  | 'templates'
  | 'activeTemplateId'
  | 'favoriteTemplateId'
  | 'onApplyTemplate'
  | 'onSetFavoriteTemplate'
  | 'onSaveTemplate'
  | 'onUpdateTemplate'
  | 'onDeleteTemplate'
  | 'isSavingTemplate'
>) => {
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const selectedId = activeTemplateId ?? DEFAULT_V3_TEMPLATE_ID
  const isDefaultSelected = selectedId === DEFAULT_V3_TEMPLATE_ID

  const entries = useMemo(
    () => [
      { id: DEFAULT_V3_TEMPLATE_ID, name: 'Default View', description: 'Built-in column layout', createdBy: '' },
      ...templates,
    ],
    [templates],
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return entries
    return entries.filter(template =>
      `${template.name} ${template.description ?? ''} ${template.createdBy ?? ''}`
        .toLowerCase()
        .includes(term),
    )
  }, [entries, search])

  return (
    <>
      <PanelHeader title='Templates' caption='Saved column layouts' />

      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          size='small'
          fullWidth
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder='Find a template'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => setSearch('')} aria-label='Clear template search'>
                  <ClearIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <List dense disablePadding>
          {filtered.map(template => {
            const isSelected = template.id === selectedId
            const isFavorite = template.id === favoriteTemplateId
            return (
              <ListItemButton
                key={template.id}
                selected={isSelected}
                onClick={() => onApplyTemplate(template.id)}
                sx={{
                  py: 0.75,
                  '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                }}
              >
                <Box sx={{ width: 24, display: 'flex', alignItems: 'center' }}>
                  {isSelected ? <CheckIcon sx={{ fontSize: '1rem' }} color='primary' /> : null}
                </Box>
                <ListItemText
                  primary={template.name}
                  secondary={template.description || template.createdBy || undefined}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, noWrap: true }}
                  secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                />
                <Tooltip title={isFavorite ? 'Favorite template' : 'Set as favorite'}>
                  <IconButton
                    size='small'
                    aria-label={isFavorite ? 'Favorite template' : `Set ${template.name} as favorite`}
                    onClick={event => {
                      event.stopPropagation()
                      onSetFavoriteTemplate(template.id)
                    }}
                    sx={{ ml: 0.5 }}
                  >
                    {isFavorite ? (
                      <StarIcon sx={{ fontSize: '1.15rem', color: 'warning.main' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: '1.15rem', color: 'action.active' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            )
          })}
          {filtered.length === 0 ? (
            <Typography variant='body2' sx={{ px: 2, py: 2, color: 'text.secondary' }}>
              No templates match “{search}”.
            </Typography>
          ) : null}
        </List>
      </Box>

      <Divider />
      <Stack spacing={1} sx={{ p: 2 }}>
        <Button
          size='small'
          variant='outlined'
          startIcon={<SaveIcon />}
          onClick={onSaveTemplate}
          disabled={isSavingTemplate}
          sx={{ textTransform: 'none' }}
        >
          Save current columns as new
        </Button>
        <Button
          size='small'
          variant='outlined'
          startIcon={<UpdateIcon />}
          onClick={onUpdateTemplate}
          disabled={isDefaultSelected || isSavingTemplate}
          sx={{ textTransform: 'none' }}
        >
          Update this template
        </Button>
        <Button
          size='small'
          variant='outlined'
          color='error'
          startIcon={<DeleteOutlineIcon />}
          onClick={onDeleteTemplate}
          disabled={isDefaultSelected}
          sx={{ textTransform: 'none' }}
        >
          Delete this template
        </Button>
      </Stack>
    </>
  )
}

const isFilterItemActive = (item: GridFilterItem | undefined): boolean => isInventoryV3FilterItemActive(item)

const FILTER_VALUE_SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  PaperProps: {
    sx: {
      maxHeight: 240,
    },
  },
  MenuListProps: { dense: true },
}

export const FiltersPanel = ({
  columns,
  filterModel,
  onFilterModelChange,
  clearAllFilters,
  hasActiveFilters,
}: Pick<InventoryV3SidebarProps, 'columns' | 'filterModel' | 'onFilterModelChange'> & {
  clearAllFilters?: () => void
  hasActiveFilters?: boolean
}) => {
  const filterable = useMemo(
    () => columns.filter(column => column.filterable !== false && !UNMANAGED_FIELDS.has(column.field)),
    [columns],
  )
  const items = filterModel.items
  const logicOperator = filterModel.logicOperator ?? GridLogicOperator.And
  const activeCount = items.filter(isFilterItemActive).length

  const [search, setSearch] = useState('')
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({})

  const visibleFilterable = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return filterable
    return filterable.filter(column =>
      `${column.headerName ?? ''} ${column.field}`.toLowerCase().includes(term),
    )
  }, [filterable, search])

  const itemByField = useMemo(() => {
    const map = new Map<string, GridFilterItem>()
    for (const item of items) {
      if (!map.has(item.field)) map.set(item.field, item)
    }
    return map
  }, [items])

  const setItems = (nextItems: GridFilterItem[]) => {
    onFilterModelChange({
      ...filterModel,
      items: nextItems,
      logicOperator: nextItems.length > 1 ? logicOperator : GridLogicOperator.And,
    })
  }

  const upsertItem = (field: string, patch: Partial<GridFilterItem> | null) => {
    const column = filterable.find(candidate => candidate.field === field)
    if (!column) return
    const existingIndex = items.findIndex(item => item.field === field)
    if (patch === null) {
      if (existingIndex < 0) return
      setItems(items.filter((_, index) => index !== existingIndex))
      return
    }
    if (existingIndex < 0) {
      setItems([...items, { ...createFilterItem(column), ...patch, field }])
      return
    }
    setItems(
      items.map((item, index) => (index === existingIndex ? { ...item, ...patch } : item)),
    )
  }

  const toggleExpanded = (field: string) => {
    setExpandedFields(prev => ({ ...prev, [field]: !(prev[field] ?? false) }))
  }

  return (
    <>
      <PanelHeader
        title='Filters'
        caption={
          activeCount > 0
            ? `${activeCount} active filter${activeCount === 1 ? '' : 's'} — expand a column to edit`
            : 'All columns listed — expand one to filter (same model as the header row)'
        }
      />

      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          size='small'
          fullWidth
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder='Find a filter'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => setSearch('')} aria-label='Clear filter search'>
                  <ClearIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
          <Button
            size='small'
            onClick={() => (clearAllFilters ? clearAllFilters() : onFilterModelChange({ items: [] }))}
            disabled={!(hasActiveFilters ?? items.length > 0)}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            Clear all
          </Button>
        </Stack>
        {items.length > 1 ? (
          <FormControl size='small' fullWidth sx={{ mt: 1 }}>
            <Select
              value={logicOperator}
              onChange={event =>
                onFilterModelChange({
                  ...filterModel,
                  logicOperator: event.target.value as GridLogicOperator,
                })
              }
            >
              <MenuItem value={GridLogicOperator.And}>Match all (AND)</MenuItem>
              <MenuItem value={GridLogicOperator.Or}>Match any (OR)</MenuItem>
            </Select>
          </FormControl>
        ) : null}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {filterable.length === 0 ? (
          <Typography variant='caption' color='text.secondary' sx={{ px: 2, py: 1.5, display: 'block' }}>
            No filterable columns.
          </Typography>
        ) : visibleFilterable.length === 0 ? (
          <Typography variant='caption' color='text.secondary' sx={{ px: 2, py: 1.5, display: 'block' }}>
            No filters match “{search}”.
          </Typography>
        ) : (
          <List dense disablePadding>
            {visibleFilterable.map(column => {
              const field = column.field
              const label = column.headerName ?? field
              const expanded = expandedFields[field] ?? false
              const item = itemByField.get(field)
              const draft = item ?? createFilterItem(column)
              const active = isFilterItemActive(item)
              const operators = operatorsForColumn(column)
              const valueOptions = valueOptionsForColumn(column)
              const operator = operators.some(candidate => candidate.value === draft.operator)
                ? draft.operator
                : (operators[0]?.value ?? 'contains')
              const needsValue = !OPERATORS_WITHOUT_VALUE.has(operator)
              const inputType =
                column.type === 'number'
                  ? 'number'
                  : column.type === 'date' || column.type === 'dateTime'
                    ? 'date'
                    : 'text'

              return (
                <Box key={field} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ListItemButton onClick={() => toggleExpanded(field)} sx={{ py: 1 }}>
                    <ListItemText
                      primary={
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <Typography variant='body2' fontWeight={active ? 600 : 400}>
                            {label}
                          </Typography>
                          {active ? <Chip size='small' label='On' color='primary' sx={{ height: 20 }} /> : null}
                        </Stack>
                      }
                    />
                    {expanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
                  </ListItemButton>
                  <Collapse in={expanded} timeout='auto' unmountOnExit>
                    <Box sx={{ px: 2, pb: 1.5 }}>
                      <Stack spacing={1}>
                        <FormControl size='small' fullWidth>
                          <Select
                            value={operator}
                            MenuProps={FILTER_VALUE_SELECT_MENU_PROPS}
                            onChange={event => {
                              const nextOperator = String(event.target.value)
                              upsertItem(field, {
                                ...draft,
                                operator: nextOperator,
                                value: normalizeFilterValueForOperator(nextOperator, draft.value),
                              })
                            }}
                          >
                            {operators.map(candidate => (
                              <MenuItem key={candidate.value} value={candidate.value}>
                                {candidate.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {needsValue ? (
                          valueOptions ? (
                            operator === 'isAnyOf' ? (
                              <FormControl size='small' fullWidth>
                                <Select
                                  multiple
                                  displayEmpty
                                  value={
                                    Array.isArray(draft.value)
                                      ? draft.value.map(String)
                                      : draft.value
                                        ? [String(draft.value)]
                                        : []
                                  }
                                  renderValue={selected => {
                                    const values = selected as string[]
                                    if (values.length === 0) return <em>Select statuses</em>
                                    return values
                                      .map(
                                        value =>
                                          valueOptions.find(option => option.value === value)?.label ??
                                          value,
                                      )
                                      .join(', ')
                                  }}
                                  MenuProps={FILTER_VALUE_SELECT_MENU_PROPS}
                                  onChange={event => {
                                    const next = event.target.value
                                    upsertItem(field, {
                                      ...draft,
                                      operator,
                                      value: typeof next === 'string' ? next.split(',') : next,
                                    })
                                  }}
                                >
                                  {valueOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                      <Checkbox
                                        size='small'
                                        checked={
                                          Array.isArray(draft.value)
                                            ? draft.value.map(String).includes(option.value)
                                            : String(draft.value ?? '') === option.value
                                        }
                                      />
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <FormControl size='small' fullWidth>
                                <Select
                                  displayEmpty
                                  value={
                                    Array.isArray(draft.value)
                                      ? (draft.value[0] ?? '')
                                      : (draft.value ?? '')
                                  }
                                  MenuProps={FILTER_VALUE_SELECT_MENU_PROPS}
                                  onChange={event =>
                                    upsertItem(field, {
                                      ...draft,
                                      operator,
                                      value: event.target.value,
                                    })
                                  }
                                >
                                  <MenuItem value=''>
                                    <em>Value</em>
                                  </MenuItem>
                                  {valueOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )
                          ) : inputType === 'date' && operator === 'between' ? (
                            <InventoryV3DateRangePicker
                              value={draft.value}
                              onChange={range =>
                                upsertItem(field, {
                                  ...draft,
                                  operator,
                                  value: range,
                                })
                              }
                            />
                          ) : inputType === 'date' ? (
                            <InventoryV3DatePicker
                              value={draft.value}
                              onChange={ymd =>
                                upsertItem(field, {
                                  ...draft,
                                  operator,
                                  value: ymd,
                                })
                              }
                            />
                          ) : (
                            <TextField
                              size='small'
                              fullWidth
                              type={inputType}
                              placeholder='Value'
                              value={draft.value ?? ''}
                              onChange={event =>
                                upsertItem(field, {
                                  ...draft,
                                  operator,
                                  value: event.target.value,
                                })
                              }
                            />
                          )
                        ) : null}
                        <Button
                          size='small'
                          color='inherit'
                          startIcon={<ClearIcon />}
                          disabled={!item}
                          onClick={() => upsertItem(field, null)}
                          sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                        >
                          Clear column
                        </Button>
                      </Stack>
                    </Box>
                  </Collapse>
                </Box>
              )
            })}
          </List>
        )}
      </Box>
    </>
  )
}

export const InventoryV3Sidebar = ({
  tab,
  onTabChange,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  pinnedColumns,
  onPinnedColumnsChange,
  onResetColumns,
  templates,
  activeTemplateId,
  favoriteTemplateId,
  onApplyTemplate,
  onSetFavoriteTemplate,
  onSaveTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  isSavingTemplate,
  selectedVehicles,
  filterModel,
  onFilterModelChange,
  clearAllFilters,
  hasActiveFilters,
  getVisibleColumnsForPdf,
}: InventoryV3SidebarProps) => {
  const theme = useTheme()

  return (
    <Box sx={{ display: 'flex', height: '100%', flexShrink: 0 }}>
      {tab !== null && (
        <Box
          sx={{
            width: PANEL_WIDTH,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderLeft: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          {tab === 'columns' && (
            <ColumnsPanel
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={onColumnVisibilityModelChange}
              pinnedColumns={pinnedColumns}
              onPinnedColumnsChange={onPinnedColumnsChange}
              onResetColumns={onResetColumns}
            />
          )}
          {tab === 'templates' && (
            <TemplatesPanel
              templates={templates}
              activeTemplateId={activeTemplateId}
              favoriteTemplateId={favoriteTemplateId}
              onApplyTemplate={onApplyTemplate}
              onSetFavoriteTemplate={onSetFavoriteTemplate}
              onSaveTemplate={onSaveTemplate}
              onUpdateTemplate={onUpdateTemplate}
              onDeleteTemplate={onDeleteTemplate}
              isSavingTemplate={isSavingTemplate}
            />
          )}
          {tab === 'filters' && (
            <FiltersPanel
              columns={columns}
              filterModel={filterModel}
              onFilterModelChange={onFilterModelChange}
              clearAllFilters={clearAllFilters}
              hasActiveFilters={hasActiveFilters}
            />
          )}
          {tab === 'actions' && (
            <>
              <PanelHeader
                title='Actions'
                caption={
                  selectedVehicles.length > 0
                    ? `${selectedVehicles.length} truck${selectedVehicles.length === 1 ? '' : 's'} selected`
                    : 'Select trucks in the grid to enable bulk actions'
                }
              />
              <Divider />
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <ActionsPanel
                  selectedRows={selectedVehicles}
                  clearAllFilters={clearAllFilters}
                  getVisibleColumnsForPdf={getVisibleColumnsForPdf}
                />
              </Box>
            </>
          )}
        </Box>
      )}

      <Stack
        spacing={0.5}
        sx={{
          width: RAIL_WIDTH,
          alignItems: 'stretch',
          pt: 0.5,
          borderLeft: 1,
          borderColor: 'divider',
          backgroundColor: theme.palette.action.hover,
        }}
      >
        {RAIL_ITEMS.map(item => {
          const isOpen = tab === item.tab
          const isActions = item.tab === 'actions'
          const isFilters = item.tab === 'filters'
          const filterCount = filterModel.items.length
          const label = (
            <Typography
              component='span'
              sx={{
                fontSize: 13,
                fontWeight: isOpen ? 700 : 600,
                letterSpacing: '0.02em',
                lineHeight: 1,
                writingMode: 'vertical-rl',
                textTransform: 'none',
                userSelect: 'none',
                color: isOpen ? 'primary.main' : 'text.primary',
              }}
            >
              {item.label}
            </Typography>
          )
          return (
            <Button
              key={item.tab}
              aria-label={item.label}
              aria-pressed={isOpen}
              onClick={() => onTabChange(isOpen ? null : item.tab)}
              sx={{
                minWidth: 0,
                width: '100%',
                px: 0.25,
                pt: 1,
                pb: 1,
                borderRadius: 1,
                color: isOpen ? theme.palette.primary.main : theme.palette.text.primary,
                backgroundColor: isOpen ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                '&:hover': {
                  backgroundColor: isOpen
                    ? alpha(theme.palette.primary.main, 0.18)
                    : theme.palette.action.selected,
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                  opacity: isOpen ? 1 : 0.85,
                },
              }}
            >
              {item.icon}
              {isActions && selectedVehicles.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {label}
                  <Box
                    component='span'
                    sx={{
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: 8,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: 10,
                      fontWeight: 600,
                      lineHeight: '16px',
                      textAlign: 'center',
                    }}
                  >
                    {selectedVehicles.length}
                  </Box>
                </Box>
              ) : isFilters && filterCount > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {label}
                  <Box
                    component='span'
                    sx={{
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: 8,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: 10,
                      fontWeight: 600,
                      lineHeight: '16px',
                      textAlign: 'center',
                    }}
                  >
                    {filterCount}
                  </Box>
                </Box>
              ) : (
                label
              )}
            </Button>
          )
        })}
      </Stack>
    </Box>
  )
}
